import { FC, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useStore } from '@hooks/useStore';
import { useS3ImageUploader } from '@hooks/useS3ImageUploader';
import CloseIcon from '@icons/close_icon.svg?component';
import DownloadIcon from '@icons/inputs/dowload_file_icon.svg?component';
import { UploadInputTransFields, IUploadInputProps } from './types';
import { uploadInputTranslations } from './translations';
import { availableFileTypes } from './constatnts';

export const UploadInput: FC<IUploadInputProps> = ({
  name,
  path,
  isInstantUpload = false,
  label,
  required = false,
  inputStyleProp,
  disabled,
}) => {
  const {
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<UploadInputTransFields>([uploadInputTranslations.name]);

  const formContext = useFormContext();

  const {
    setValue,
    formState: { errors },
  } = formContext;

  const { uploadImage } = useS3ImageUploader<Record<string, string>>((val) => {
    if (val[name]) setValue(name, val[name]);
  }, path);

  const onDrop = useCallback(async (acceptedFiles: any) => {
    if (acceptedFiles && acceptedFiles[0]) {
      const file = acceptedFiles[0];
      if (!availableFileTypes.includes(file.type)) {
        showAlert(t('incorrectFileType'), 'warning');
        return;
      }
      if (isInstantUpload) {
        try {
          await uploadImage({}, file, name);
          showAlert(t('imageUploaded'), 'success');
        } catch {
          showAlert(t('cannotUploadImage'), 'warning');
        }
      } else {
        setValue(name, URL.createObjectURL(file), { shouldDirty: true });
        setValue(`${name}File`, file, { shouldDirty: true });
      }
    }
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({ onDrop, noClick: true });

  const getContent = useCallback(
    (value: string | undefined | null) => {
      if (value) {
        return (
          <div className="h-full w-full p-[30px] bg-[#F3F5FC] rounded-[8px] relative">
            <img src={value} alt="point image" className="h-full w-full object-contain" />
            {!disabled && (
              <button
                className="p-0 cursor-pointer absolute bg-none bottom-[16px] right-[16px] rounded-[50%] transition-all hover:bg-[rgba(white, 0.3)]"
                type="button"
                onClick={() => {
                  setValue(name, null, { shouldDirty: true });
                  setValue(`${name}File`, null, { shouldDirty: true });
                }}
                disabled={disabled}
              >
                <CloseIcon width={16} height={16} />
              </button>
            )}
          </div>
        );
      } else if (!disabled) {
        return (
          <div
            className={`h-full w-full rounded-[8px] bg-[rgba(246, 248, 255, 0.5)] border border-dashed border-[#C9CAD0] bg-[#F6F8FF80] flex flex-col justify-center items-center px-[12px] text-center ${
              errors[name] && 'border-error'
            }`}
          >
            <DownloadIcon width={48} height={48} />
            <p className="small-text uppercase mt-[16px]">{t('firstRow')}</p>
            <p className="small-text uppercase mt-[8px]">
              <span>{t('secondRow')}&nbsp; </span>
              <button
                type="button"
                onClick={open}
                className="cursor-pointer p-0 bg-none uppercase text-primary"
                disabled={disabled}
              >
                {t('btnText')}
              </button>
            </p>
            <p className="small-text opacity-40 mt-[8px]">{t('lastRow')}: JPG, PNG</p>
          </div>
        );
      } else {
        return (
          <div
            className={`h-full w-full rounded-[8px] bg-[rgba(246, 248, 255, 0.5)] border-dashed border-[#c9cad0] bg-[#F6F8FF80] flex flex-col justify-center items-center px-[12px] text-center`}
          >
            <p className="default-text uppercase mt-[16px]">{t('noImageRow')}</p>
          </div>
        );
      }
    },
    [disabled, errors]
  );

  return (
    <div
      // className="flex flex-col justify-center items-start w-[240px] h-[240px]"
      className="flex flex-col justify-center items-start h-[240px]"
      {...getRootProps()}
    >
      {label && <p className="subtitle">{label}</p>}
      <Controller
        control={formContext?.control}
        name={name}
        rules={{ required: required }}
        render={({ field: { value } }) => (
          <>
            <input
              {...getInputProps()}
              className={`opacity-0 w-0 h-0 overflow-hidden absolute -z-10 ${inputStyleProp}`}
              type="file"
              id={name}
              accept="image/*"
              required={required}
              disabled={disabled}
            />
            {getContent(value)}
          </>
        )}
      />
    </div>
  );
};
